"use client";

import { cn } from "@devhop/ui";
import { HistoryIcon, Loader2Icon, UserIcon } from "lucide-react";
import type { PosSaleActivityItem } from "../api/client";
import { usePosSaleActivityQuery } from "../api/queries";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  posSaleAuditActionLabel,
  posSaleAuditDetail,
} from "../lib/pos-sale-audit-labels";

export function PosSaleActivitySection({
  shopId,
  saleId,
  className,
}: {
  shopId: string;
  saleId: string;
  className?: string;
}) {
  const activityQ = usePosSaleActivityQuery(shopId, saleId);
  const items = activityQ.data?.items ?? [];

  return (
    <section className={cn("space-y-2", className)}>
      <h3 className="flex items-center gap-2 font-heading font-semibold text-card-foreground text-xs uppercase tracking-wide">
        <HistoryIcon
          className="size-4 shrink-0 text-[hsl(210_90%_48%)]"
          aria-hidden
        />
        ປະຫວັດການດຳເນີນການ
      </h3>
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        {activityQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-8 text-muted-foreground text-sm">
            <Loader2Icon className="size-4 animate-spin" />
            ກຳລັງໂຫຼດ…
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີບັນທຶກການກວດກາ
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <PosSaleActivityRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function PosSaleActivityRow({ item }: { item: PosSaleActivityItem }) {
  const detail = posSaleAuditDetail(item.before, item.after, item.meta);

  return (
    <li className="flex gap-3 px-4 py-3">
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
        <UserIcon className="size-3.5 text-muted-foreground" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
          <p className="font-medium text-sm">
            {posSaleAuditActionLabel(item.action)}
          </p>
          <time className="shrink-0 text-muted-foreground text-[11px] tabular-nums">
            {formatDateTimeLocal(item.occurredAt)}
          </time>
        </div>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {item.actorName?.trim() || item.actorId || "—"}
        </p>
        {detail ? (
          <p className="mt-1 text-foreground/80 text-xs">{detail}</p>
        ) : null}
      </div>
    </li>
  );
}
